import mixpanel from "mixpanel-browser";
import { useEffect } from "react";
import { useIntercom } from "react-use-intercom";

import type { EscalationRequest } from "./use-companion-v2";

function buildPrefill({
  lastUserMessage,
  lastAssistantMessage,
  agentSessionUuid,
  summary,
  reason,
}: EscalationRequest): string {
  const lines: string[] = [
    "Your Companion has drafted this message for the support team. " +
      "Review and add anything else you'd like them to know, then " +
      "click Send — your Companion can't send it on your behalf.",
    "",
    "---",
  ];
  if (summary) {
    lines.push("", summary);
  }
  if (lastUserMessage) {
    lines.push("", `What I asked my Companion: ${lastUserMessage}`);
  }
  if (lastAssistantMessage) {
    lines.push("", `My Companion's reply: ${lastAssistantMessage}`);
  }
  if (reason || agentSessionUuid) {
    lines.push("", "---");
    if (reason) lines.push(`Reason: ${reason}`);
    if (agentSessionUuid) lines.push(`Session: ${agentSessionUuid}`);
  }
  return lines.join("\n");
}

// Effect-driven hook: pass the pending escalation request from
// useCompanionV2 + a callback to clear it once handled. The hook owns
// the "fire once per request, then clear" lifecycle so consumers don't
// have to wire up a useEffect themselves.
//
// Two modes: when the backend auto-created an Intercom conversation
// (request.conversationId is set), open that thread directly via
// showConversation — the drafted message is already posted as-the-user.
// Otherwise fall back to showNewMessage(prefill): Intercom's JS SDK
// can't auto-submit on the user's behalf, so the user reviews the
// pre-drafted text and clicks Send themselves.
export function useEscalateToSupport({
  request,
  onFired,
}: {
  request: EscalationRequest | null;
  onFired: () => void;
}): void {
  const { showNewMessage, showConversation, trackEvent, update } =
    useIntercom();

  useEffect(() => {
    if (!request) return;
    trackEvent("companion_v2_escalated_to_human", {
      agent_session_uuid: request.agentSessionUuid ?? null,
      reason: request.reason ?? null,
      autosent: !!request.conversationId,
    });
    mixpanel.track("Companion V2 Escalated To Human", {
      agentSessionUuid: request.agentSessionUuid,
      reason: request.reason,
      autosent: !!request.conversationId,
    });
    // customAttributes surface on the Intercom conversation so the
    // support team sees the companion session UUID + escalation reason
    // directly in their dashboard, not just buried in the message body.
    // The launcher is always visible for returning users to access old
    // threads; "start a new conversation" is gated in the Intercom
    // workspace settings so users route new asks through Otto.
    update({
      customAttributes: {
        companion_session_uuid: request.agentSessionUuid ?? null,
        companion_escalation_reason: request.reason ?? null,
      },
    });
    // react-use-intercom types showConversation as (id: number) — Intercom
    // REST returns the id as a numeric string, so convert & guard.
    const numericConversationId = request.conversationId
      ? Number(request.conversationId)
      : NaN;
    if (Number.isFinite(numericConversationId)) {
      showConversation(numericConversationId);
    } else {
      showNewMessage(buildPrefill(request));
    }
    onFired();
  }, [
    request,
    onFired,
    showNewMessage,
    showConversation,
    trackEvent,
    update,
  ]);
}
